import React, { useCallback, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import B2bTDPAPI from 'api/b2b/TdpApi'
import WalletAdjustmentApi, {
  IWalletAdjustmentSetBackendDataPayload
} from 'api/b2b/WalletAdjustment'
import OrbitSession from 'hooks/useCookies'

interface Company {
  tenant_id: string
  company_name: string
  id: string
}

interface TenantData {
  data: Company[]
}

interface CompanyOption {
  label: string
  value: string
  companyId: string
}

interface FormData {
  companyName: CompanyOption | null
  companyId: string
  referenceNumber: string
  adjustmentType: string
  transactionAmount: string
  note: string
}



export default function AddStatement() {
  const navigate = useNavigate()

  const {
    data: tenantData,
    isLoading: isTenantLoading,
    isError: tenantFetchError,
    refetch: refetchTenant
  } = useQuery<TenantData>(
    ['tenant-detail'],
    async () => {
      const token = OrbitSession.get('faberToken')
      const { data } = await B2bTDPAPI.getTDPDetails(token)
      return data
    },
    {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      refetchOnWindowFocus: false
    }
  )

  const { mutate: addWalletAdjustment } = useMutation({
    mutationFn: async (payload: IWalletAdjustmentSetBackendDataPayload) => {
      const token = OrbitSession.get('faberToken')
      return WalletAdjustmentApi.addWalletAdjustment(token, payload)
    },
    onSuccess: () => {
      toast.success('Wallet adjustment added successfully')
      navigate('/hq/b2b/wallet-adjustment')
    },
    onError: () => {
      toast.error('Failed to add wallet adjustment')
    }
  })

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    // formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      companyName: null,
      companyId: '',
      referenceNumber: '',
      adjustmentType: '',
      transactionAmount: '',
      note: ''
    }
  })

  // Watch form values
  const companyNameInput = watch('companyName')

  // Memoized company options to avoid recalculation on every render
  const companyOptions = useMemo((): CompanyOption[] => {
    if (!tenantData || !Array.isArray(tenantData)) return []

    return tenantData.map((company: Company) => ({
      label: company.company_name || '',
      value: company.company_name || '',
      companyId: company.id || ''
    }))
  }, [tenantData])

  // Auto-populate company ID when company is selected
  useEffect(() => {
    if (companyNameInput?.companyId) {
      setValue('companyId', companyNameInput.companyId)
    } else {
      setValue('companyId', '')
    }
  }, [companyNameInput, setValue])

  // Optimized autocomplete filter function
  const filterOptions = useCallback(
    (options: CompanyOption[], { inputValue }: { inputValue: string }) => {
      if (!inputValue) return options.slice(0, 100) // Limiting initial results

      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      )
      return filtered.slice(0, 100) // Limiting filtered results
    },
    []
  )

  const onSubmit = (data: FormData) => {
    // Transform form data to match the required payload format
    const payload: IWalletAdjustmentSetBackendDataPayload = {
      tenant_id: data.companyId,
      transaction_amount: parseFloat(data.transactionAmount),
      type: data.adjustmentType === 'Credit' ? 'CR' : 'DR',
      reference_no: data.referenceNumber,
      created_by: OrbitSession.get('email') || '',
      comment: data.note || ''
    }
    addWalletAdjustment(payload)
  }

  if (isTenantLoading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading company data...</Typography>
      </Box>
    )
  }

  if (tenantFetchError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color='error'>Failed to load company data</Typography>
        <Button
          onClick={() => refetchTenant()}
          variant='outlined'
          sx={{ mt: 1 }}
        >
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 2 }}>
        Add Wallet Adjustment
      </Typography>

      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          backgroundColor: '#fff',
          p: 2,
          borderRadius: 2
        }}
      >
        <Grid container spacing={2}>
          {/* Row 1 */}
          <Grid item xs={12} md={4}>
            <Controller
              name='companyName'
              control={control}
              rules={{
                required: { value: true, message: 'Company name is required' }
              }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  disablePortal
                  options={companyOptions}
                  filterOptions={filterOptions}
                  loading={isTenantLoading}
                  getOptionLabel={(option: CompanyOption | null) =>
                    option?.label || ''
                  }
                  isOptionEqualToValue={(
                    option: CompanyOption,
                    value: CompanyOption | null
                  ) => option?.value === value?.value}
                  onChange={(_, data: CompanyOption | null) =>
                    field.onChange(data)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Company Name *'
                      size='small'
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isTenantLoading ? (
                              <CircularProgress color='inherit' size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name='companyId'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label='Company ID'
                  size='small'
                  disabled={true}
                  fullWidth
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' // Better visibility for disabled text
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name='referenceNumber'
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Reference number is required'
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label='Reference Number *'
                  size='small'
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  placeholder='e.g., BANK-TXN-123456'
                />
              )}
            />
          </Grid>

          {/* Row 2 */}
          <Grid item xs={12} md={4}>
            <Controller
              name='adjustmentType'
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Adjustment type is required'
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  select
                  label='Adjustment Type *'
                  size='small'
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                >
                  <MenuItem value='Credit'>Credit</MenuItem>
                  <MenuItem value='Debit'>Debit</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name='transactionAmount'
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Transaction amount is required'
                },
                min: { value: 0.01, message: 'Amount must be greater than 0' },
                pattern: {
                  value: /^\d+(\.\d{1,2})?$/,
                  message: 'Please enter a valid amount (max 2 decimal places)'
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label='Transaction Amount *'
                  type='number'
                  inputProps={{ step: '0.01', min: '0.01' }}
                  size='small'
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  placeholder='e.g., 500.00'
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name='note'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label='Comment'
                  multiline
                  rows={2}
                  size='small'
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  placeholder='Comment about this adjustment'
                />
              )}
            />
          </Grid>

          {/* Buttons */}
          <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Button
              variant='contained'
              color='primary'
              type='submit'
              sx={{ textTransform: 'none' }}
              disabled={!companyNameInput?.value} // Remove isSubmitting when API is ready
            >
              Save
            </Button>
            <Button
              variant='outlined'
              color='primary'
              type='button'
              onClick={() => navigate('/hq/b2b/wallet-adjustment')}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
